import { c as _c } from "react/compiler-runtime";
import { AsyncMultiChoice, Chips, Separator, useTranslationContext } from 'platform/components';
import { HStack, VStack } from 'platform/foundation-tw';
import { match } from 'ts-pattern';
import { useRef } from 'react';
import { always } from 'ramda';
import { isArray, isNilOrEmpty, isNotNilOrEmpty, isString } from 'ramda-adjunct';
import { suffixTestId } from 'shared/web';
import { useHttpCalls } from '../../api/useHttpCalls';
import { useFilterOnChipsChange } from '../../hooks/useFilterOnChipsChange';
import type { EagFilterProps } from '../../types/EagFilterProps';
import { getFilterFieldName } from '../../utils/getFilterFieldName';
const EMPTY_VALUE = decodeURI('%00');
const FILTER_VALUE_KEY = 'value';
export type SuggestionFilterProps = EagFilterProps & {
  suggestId: string;
  quickFilters?: Array<{
    value: string;
    label: string;
  }>;
};
export function SuggestionFilter(props) {
  const $ = _c(44);
  const http = useHttpCalls();
  const t = useTranslationContext();
  const isActive = useRef(false);
  const model = props.value[FILTER_VALUE_KEY] as string[] | string | null;
  let t0;
  if ($[0] !== props) {
    t0 = values => {
      isActive.current = isNotNilOrEmpty(values);
      props.onChange({
        [FILTER_VALUE_KEY]: values
      });
    };
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setSelectedIds = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== setSelectedIds) {
    t2 = {
      setFilterValue: setSelectedIds,
      defaultValue: t1
    };
    $[3] = setSelectedIds;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    onChipsChange
  } = useFilterOnChipsChange(t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const optionsCacheRef = useRef(t3);
  let t4;
  if ($[6] !== http || $[7] !== props.suggestId) {
    t4 = async query => {
      const cacheKey = `${props.suggestId}:${query}`;
      const cached = optionsCacheRef.current.get(cacheKey);
      if (cached !== undefined) {
        return cached;
      }
      const getOptionsArgs = {
        suggestId: props.suggestId,
        inputValue: isNilOrEmpty(query) ? EMPTY_VALUE : query,
        limit: 20
      };
      const result = await http.getSuggestionOptions(getOptionsArgs);
      const options = result ?? [];
      if (optionsCacheRef.current.size >= 20) {
        const firstKey = optionsCacheRef.current.keys().next().value;
        if (firstKey !== undefined) {
          optionsCacheRef.current.delete(firstKey);
        }
      }
      optionsCacheRef.current.set(cacheKey, options);
      return options;
    };
    $[6] = http;
    $[7] = props.suggestId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const fetchOptions = t4;
  let t5;
  if ($[9] !== http || $[10] !== props.suggestId) {
    t5 = async values_0 => {
      const getLabelsArgs = {
        suggestId: props.suggestId,
        keys: values_0
      };
      const result_0 = await http.getSuggestionLabels(getLabelsArgs);
      return result_0 ?? [];
    };
    $[9] = http;
    $[10] = props.suggestId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const fetchLabels = t5;
  let t6;
  if ($[12] !== setSelectedIds) {
    t6 = values_1 => {
      const newSelectedIds = match(values_1).when(isNotNilOrEmpty, _temp).otherwise(always(null));
      setSelectedIds(newSelectedIds);
    };
    $[12] = setSelectedIds;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const onChange = t6;
  let t7;
  if ($[14] !== model) {
    t7 = isString(model) ? [model] : null;
    $[14] = model;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const isQuickFilterValue = t7;
  let t8;
  if ($[16] !== isQuickFilterValue || $[17] !== onChipsChange || $[18] !== props.isDisabled || $[19] !== props.quickFilters) {
    t8 = props.quickFilters && props.quickFilters.length > 0 && <><HStack><Chips data-testid="quick-filters-suggestion-filter" isDisabled={props.isDisabled} value={isQuickFilterValue} options={props.quickFilters} onChange={onChipsChange} isMultiple={false} isDeselectable={true} /></HStack><Separator /></>;
    $[16] = isQuickFilterValue;
    $[17] = onChipsChange;
    $[18] = props.isDisabled;
    $[19] = props.quickFilters;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== model) {
    t9 = isArray(model) ? model : [model].filter(Boolean) as string[];
    $[21] = model;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== fetchLabels) {
    t10 = values_2 => fetchLabels(values_2 as string[]);
    $[23] = fetchLabels;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t) {
    t11 = () => t("page.datagrid.labels.noSuggestFilterOptions");
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== props.column || $[28] !== t) {
    t12 = t("page.datagrid.filter.suggestPlaceholder", {
      field: getFilterFieldName(props.column)
    });
    $[27] = props.column;
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = props.isDisabled;
  let t14;
  if ($[30] !== props) {
    t14 = suffixTestId("multi-choice-suggestion-filter", props);
    $[30] = props;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== fetchOptions || $[33] !== onChange || $[34] !== props.isDisabled || $[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t14 || $[39] !== t9) {
    t15 = <AsyncMultiChoice value={t9} onChange={onChange} loadOptions={fetchOptions} loadLabels={t10} defaultOptions={true} noOptionsMessage={t11} placeholder={t12} isDisabled={t13} isNotClearable={true} menuInPortal={true} data-testid={t14} />;
    $[32] = fetchOptions;
    $[33] = onChange;
    $[34] = props.isDisabled;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t14;
    $[39] = t9;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t15 || $[42] !== t8) {
    t16 = <VStack>{t8}{t15}</VStack>;
    $[41] = t15;
    $[42] = t8;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
}
function _temp(val) {
  return val;
}